import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useObserver } from 'mobx-react-lite';

import { useModals, ModalNames } from 'components/modals';
import useUser from 'hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { usePhoneVerificationNudgeOperational } from 'src/hooks/use-phone-verification-nudge-operational';
import { usePhoneVerification } from 'src/components/modals/phone-verification-nudge/use-phone-verification';
import {
  deriveNudgeLeg,
  parseSendStatus,
  parseStatusValue,
} from 'src/components/modals/phone-verification-nudge/phone-verification.types';
import type { OtpHostContext, OtpHostOutcome } from './otp-host';

/**
 * The banner is a projection of `phoneVerificationStatus`, not a one-shot "should we show a CTA"
 * flag -- `leg_one` (unverified, phone on file) gets the actionable "Verify Now" row;
 * `verified_other` gets an informational row naming the account that owns the number, using the
 * `ownerEmailHint` that's already present on that same status response. Every other status
 * (`add_phone`, `verified_self`, `nudge_disabled`, unknown, or no response at all) renders nothing.
 */
export type CtaState =
  | { kind: 'leg_one'; dispensaryId?: string | null }
  | { kind: 'verified_other'; dispensaryId?: string | null; ownerEmailHint?: string | null };

export type BannerErrorKind = 'invalid_number' | 'rate_limited' | 'unavailable';

export type BannerError = {
  kind: BannerErrorKind;
};

export type UsePhoneVerificationCtaResult = {
  isVisible: boolean;
  isSending: boolean;
  cta: CtaState | null;
  bannerError: BannerError | null;
  onVerifyClick: () => Promise<void>;
};

type SendPayload =
  | {
      status?: string | null;
      phoneHint?: string | null;
      errorCode?: string | null;
    }
  | null
  | undefined;

/**
 * Maps a `sendPhoneVerificationCode` result that ISN'T `sent` onto a banner error variant. Under
 * the FINTECH-4689 contract, rate limits and invalid/undeliverable numbers arrive as a 2xx
 * carrying `errorCode` rather than a thrown error; `quarantined`/`not_verifiable`/`nudge_disabled`
 * arrive as `status`. Anything unrecognized fails safe to the generic "unavailable" copy rather
 * than a silent no-op banner.
 */
function mapSendFailure(payload: SendPayload): BannerError {
  if (payload?.errorCode === 'rate_limited') {
    return { kind: 'rate_limited' };
  }
  if (payload?.errorCode === 'invalid_number' || payload?.errorCode === 'undeliverable') {
    return { kind: 'invalid_number' };
  }
  return { kind: 'unavailable' };
}

/**
 * Gating + status fetch + click/outcome logic for the "Verify your phone number" banner on My
 * Account. The banner is a projection of the latest `phoneVerificationStatus` response --
 * `leg_one` renders the actionable row, `verified_other` renders the informational
 * belongs-to-another-account row (using the hint already present on that response), and every
 * other status renders nothing. Deliberately independent of `usePhoneVerificationNudge` (the
 * post-login orchestrator): that hook bails on `UI.isCheckoutPage`, `Modals.activeModals.size > 0`,
 * and its own in-flight ref, none of which should block a deliberate user click on this CTA.
 */
export function usePhoneVerificationCta(): UsePhoneVerificationCtaResult {
  const { openModal } = useModals();
  const user = useUser();
  const isLoggedIn = useObserver(() => user.exists);
  // Observed so the status-fetch effect below re-runs whenever the phone changes -- e.g. after a
  // My Account save unverifies a newly-changed number (`updateProfileV2` + `user.refetch()` in
  // `my-account.jsx`). `user.profile` can be `{}` before the user has loaded, hence the optional
  // chain (same pattern as `payments/dutchie-pay/signup/views/aggregator-widget/index.tsx`).
  const phone = useObserver(() => user.profile?.phone);
  // `useDispensary` is an untyped JS hook whose return shape narrows based on whether a `cName`
  // route param is present -- on `/account/*` (no `cName`) it omits `dispensaryId` entirely and
  // returns an `error` on the result instead (see `use-dispensary.js`). That `error` is the
  // normal dutchie.com case here, not a failure -- it is deliberately not read.
  const { dispensaryId } = useDispensary() as { dispensaryId?: string | null };
  const { fetchStatus, sendCode } = usePhoneVerification();
  const operational = usePhoneVerificationNudgeOperational();

  const [cta, setCta] = useState<CtaState | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [bannerError, setBannerError] = useState<BannerError | null>(null);
  // Bumped to force the status-fetch effect below to re-run on demand -- the same mechanism the
  // effect already uses when the observed phone changes, reused here so a verify-time `conflict`
  // doesn't need its own bespoke fetch: it just invalidates status and lets the projection recompute.
  const [refreshToken, setRefreshToken] = useState(0);
  const requestIdRef = useRef(0);
  // Set immediately before bumping `refreshToken` from a `conflict` outcome, and read (then
  // cleared) inside the effect run it triggers. Its only job is to pick the right failure
  // fallback: a `conflict` just told the user their number belongs to someone else, so if the
  // recovery fetch itself throws, degrade to the hint-less copy rather than the normal silent
  // "hide the banner" posture used for every other fetch failure.
  const conflictRecoveryRef = useRef(false);

  useEffect(() => {
    // Bump the request id on every effect run (including the early-return branch) so any
    // still-in-flight response from a previous run is recognized as stale and ignored.
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const isConflictRecovery = conflictRecoveryRef.current;
    let cancelled = false;

    if (!isLoggedIn || !operational) {
      // Logged-out or flag-off: clear to hidden and issue no request at all. The flag check
      // gates the effect BEFORE `fetchStatus` is ever called -- its only job is to skip the
      // network call, not to make a rollout decision.
      setCta(null);
      conflictRecoveryRef.current = false;
      return () => {
        cancelled = true;
      };
    }

    const run = async (): Promise<void> => {
      try {
        const { phoneVerificationStatus } = await fetchStatus(dispensaryId);

        if (cancelled || requestId !== requestIdRef.current) {
          // A newer effect run has already superseded this one -- do not clobber its result.
          return;
        }

        // A fresh, network-verified status supersedes any stale UI-only error banner from a
        // previous click (e.g. the user fixed their number and it's unverified again).
        setBannerError(null);

        const leg = deriveNudgeLeg(phoneVerificationStatus?.status, phoneVerificationStatus?.hasPhone);
        const status = parseStatusValue(phoneVerificationStatus?.status);

        if (leg === 'leg_one') {
          // Store the exact dispensaryId used for THIS fetch, so `sendCode`/`verifyCode` always
          // agree with what `fetchStatus` was called with -- never re-read `useDispensary()` at
          // click time (Armageddon's `PhoneNumbers::VerificationGate` branches on it).
          setCta({ kind: 'leg_one', dispensaryId });
        } else if (status === 'verified_other') {
          setCta({
            kind: 'verified_other',
            dispensaryId,
            ownerEmailHint: phoneVerificationStatus?.ownerEmailHint ?? null,
          });
        } else {
          setCta(null);
        }
      } catch {
        if (!cancelled && requestId === requestIdRef.current) {
          if (isConflictRecovery) {
            // The verify-time conflict already told us the number belongs to someone else --
            // if the recovery fetch itself fails, still show the (hint-less) informational row
            // rather than leaving the user with a blank page after a deliberate action.
            setCta({ kind: 'verified_other', dispensaryId, ownerEmailHint: null });
          } else {
            // Silent by design -- no toast, no ernie, no user-visible error. Same posture as the
            // post-login orchestrator.
            setCta(null);
          }
        }
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          conflictRecoveryRef.current = false;
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, operational, dispensaryId, phone, refreshToken]);

  const onVerifyClick = useCallback(async (): Promise<void> => {
    if (cta?.kind !== 'leg_one' || isSending) {
      // Guards against a double-send on rapid clicks -- `isSending` flips true synchronously
      // below, before the first `await`. Also guards against a stray click while the
      // informational (buttonless) row is showing -- it renders no button, but this stays
      // defensive in case that ever changes.
      return;
    }

    setIsSending(true);
    try {
      const result = await sendCode(cta.dispensaryId);
      const payload = result.sendPhoneVerificationCode;

      if (parseSendStatus(payload?.status) !== 'sent') {
        // Anything other than a confirmed `sent` opens nothing -- there is no in-flight
        // placeholder and nothing can resize.
        setBannerError(mapSendFailure(payload));
        return;
      }

      setBannerError(null);

      const context: OtpHostContext = {
        phoneHint: payload?.phoneHint ?? null,
        dispensaryId: cta.dispensaryId,
      };

      // The send has already completed by this point -- clear the loading state here, before
      // opening the modal, rather than waiting for `openModal` to resolve (i.e. for the modal to
      // CLOSE). Otherwise the button keeps spinning, hidden behind the modal overlay, for the
      // entire time the OTP dialog is open. `isSending` is only ever read to gate a NEW click
      // (above), and the modal overlay itself is what actually prevents interaction with the
      // banner while it's open, so clearing it here does not reopen the double-send window this
      // guard exists for. The `finally` below still clears it on every other (failure) path.
      setIsSending(false);

      const outcome = (await openModal<OtpHostContext, OtpHostOutcome>(ModalNames.phoneVerificationOtp, context)) as
        | OtpHostOutcome
        | undefined;

      if (outcome?.kind === 'verified') {
        setCta(null);
        user.refetch();
        return;
      }

      if (outcome?.kind === 'conflict') {
        // No bespoke handling: invalidate status and let the same effect that already re-derives
        // on a phone change recompute the banner. It will re-fetch, see `verified_other`, and
        // project the informational row from the `ownerEmailHint` that response carries.
        conflictRecoveryRef.current = true;
        setRefreshToken((token) => token + 1);
        return;
      }

      if (outcome?.kind === 'unavailable') {
        setBannerError({ kind: 'unavailable' });
      }

      // 'dismissed' or undefined: leave the CTA in its default state.
    } catch {
      // A thrown error from `sendCode` itself (a genuine system fault) -- silent posture is not
      // appropriate here since the user just took a deliberate action; show the generic
      // unavailable state rather than a dead click.
      setBannerError({ kind: 'unavailable' });
    } finally {
      setIsSending(false);
    }
  }, [cta, isSending, sendCode, openModal, user]);

  return useMemo(
    () => ({ isVisible: cta !== null || bannerError !== null, isSending, cta, bannerError, onVerifyClick }),
    [cta, bannerError, isSending, onVerifyClick]
  );
}
